/**
 * Cross-browser / cross-device compatibility primitives.
 *
 * Centralizes the browser-quirk workarounds the app relies on so behavior is
 * identical on every device instead of depending on per-browser defaults:
 *
 *  - formatTimestamp:        deterministic date/time (no locale drift between
 *                            a user's phones — the core watermark needs this).
 *  - MAX_CANVAS_AREA / fit*: guard against iOS Safari's silent canvas-area cap
 *                            that turns large exports into blank images.
 *  - safeCanvasToDataURL:    null/throw-safe canvas export with a PNG fallback.
 *  - copyToClipboard:        async Clipboard API with an execCommand fallback
 *                            for non-secure contexts / older Safari.
 *
 * Pure, dependency-free, and safe to import anywhere (including web workers,
 * where `document` is guarded).
 */

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

/**
 * Deterministic local-time timestamp, formatted identically on every browser
 * and locale: `M/D/YYYY, h:mm:ss AM/PM`.
 *
 * `Date.prototype.toLocaleString()` (which this replaces across the app) yields
 * different strings on iOS Safari vs Android Chrome vs desktop and shifts with
 * the device locale — so the same photo could carry differently-formatted
 * stamps on a user's two phones. This keeps the visual shape the watermark and
 * UI already expect (including the `", "` separator the information module
 * splits on) while making it device-independent.
 */
export function formatTimestamp(date: Date = new Date()): string {
  // Guard against invalid Date (e.g. from a bad parse) so we never render "NaN".
  if (Number.isNaN(date.getTime())) date = new Date();

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;

  return `${month}/${day}/${year}, ${hours}:${pad2(minutes)}:${pad2(seconds)} ${meridiem}`;
}

/**
 * Maximum total canvas area (in device pixels) we allow before downscaling.
 *
 * iOS Safari silently fails to render canvases above ~16.7M px² and ~4096px on
 * the weakest devices; modern iPhones/iPads allow more but the failure mode is
 * a *blank* image with no error. 16,777,216 (16M) is the broadly-safe ceiling.
 */
export const MAX_CANVAS_AREA = 16_777_216;

/** Maximum length of a single canvas side on the most constrained mobile GPUs. */
export const MAX_CANVAS_SIDE = 8192;

/**
 * Given a desired canvas width/height, return a scale factor (<= 1) that keeps
 * the canvas under both the per-side and total-area limits while preserving
 * aspect ratio. Returns 1 when the dimensions are already safe, so callers can
 * cheaply no-op on the common path.
 */
export function canvasFitScale(width: number, height: number): number {
  if (width <= 0 || height <= 0) return 1;

  let scale = 1;

  const longestSide = Math.max(width, height);
  if (longestSide > MAX_CANVAS_SIDE) {
    scale = Math.min(scale, MAX_CANVAS_SIDE / longestSide);
  }

  const area = width * height;
  if (area > MAX_CANVAS_AREA) {
    scale = Math.min(scale, Math.sqrt(MAX_CANVAS_AREA / area));
  }

  return scale;
}

/**
 * Downscale an image source onto a fresh canvas if (and only if) it would
 * otherwise exceed the canvas limits, returning a drawable whose dimensions are
 * safe on every device. Returns the original image untouched when it already
 * fits — so the watermark math downstream (which derives every coordinate from
 * the source width/height) scales proportionally and the layout is identical,
 * just at a safe resolution.
 */
export function fitImageToCanvasLimit(
  image: HTMLImageElement | HTMLCanvasElement
): HTMLImageElement | HTMLCanvasElement {
  const width = 'naturalWidth' in image ? image.naturalWidth || image.width : image.width;
  const height = 'naturalHeight' in image ? image.naturalHeight || image.height : image.height;

  const scale = canvasFitScale(width, height);
  if (scale >= 1) return image;

  const target = document.createElement('canvas');
  target.width = Math.max(1, Math.floor(width * scale));
  target.height = Math.max(1, Math.floor(height * scale));

  const ctx = target.getContext('2d');
  if (!ctx) return image; // Can't downscale — let the caller proceed as before.

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, 0, 0, target.width, target.height);
  return target;
}

/**
 * Export a canvas to a data URL without throwing. iOS Safari can throw (or
 * return an empty string) when a JPEG encode fails on a large/tainted canvas.
 * Falls back to PNG, then to an empty string, so callers never crash mid-export.
 */
export function safeCanvasToDataURL(
  canvas: HTMLCanvasElement,
  type: string = 'image/jpeg',
  quality: number = 0.95
): string {
  try {
    const url = canvas.toDataURL(type, quality);
    if (url && url.length > 'data:,'.length) return url;
  } catch {
    /* fall through to PNG */
  }
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return '';
  }
}

/**
 * Promise-based `canvas.toBlob` that resolves to `null` (instead of hanging or
 * throwing) when the browser fails to encode. iOS Safari can invoke the
 * callback with `null`; some engines lack `toBlob` entirely, so we synthesize a
 * Blob from the data URL as a last resort.
 */
export function safeCanvasToBlob(
  canvas: HTMLCanvasElement,
  type: string = 'image/jpeg',
  quality: number = 0.95
): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      if (typeof canvas.toBlob !== 'function') {
        resolve(dataURLToBlob(safeCanvasToDataURL(canvas, type, quality)));
        return;
      }
      canvas.toBlob(
        (blob) => resolve(blob ?? dataURLToBlob(safeCanvasToDataURL(canvas, type, quality))),
        type,
        quality
      );
    } catch {
      resolve(dataURLToBlob(safeCanvasToDataURL(canvas, type, quality)));
    }
  });
}

/**
 * Trigger a browser download of an image across devices.
 *
 * Data URLs are first converted to a Blob object URL: the `<a download>`
 * attribute is honored far more reliably for object URLs than for very large
 * `data:` URLs (iOS Safari can open the image in place or silently ignore the
 * download for multi-MB data URLs). Falls back to the raw source if conversion
 * isn't possible, and revokes the object URL after the download has started.
 */
export function downloadImage(source: string, filename: string): void {
  let href = source;
  let objectUrl: string | null = null;

  if (source.startsWith('data:')) {
    const blob = dataURLToBlob(source);
    if (blob) {
      objectUrl = URL.createObjectURL(blob);
      href = objectUrl;
    }
  }

  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Revoke after a delay so the download has time to begin (immediate revoke
  // aborts the download in some browsers).
  if (objectUrl) {
    const toRevoke = objectUrl;
    setTimeout(() => URL.revokeObjectURL(toRevoke), 10000);
  }
}

/** True on iPhone/iPad (including iPadOS masquerading as macOS). */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** True when running as an installed home-screen web app (standalone). */
export function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export type SaveImageResult = 'saved' | 'needs-gesture';

/**
 * Save an image the way the current platform actually supports.
 *
 * In an iOS HOME-SCREEN (standalone) web app the `<a download>` route is a
 * trap: it navigates to a bare blob viewer with no back button, killing the
 * camera feed and stranding the user outside the app. There Apple's only
 * supported path is the native share sheet (`navigator.share` with a File),
 * whose "Save Image" writes to Photos and returns to the app — one tap is
 * the platform minimum; silent saving is not permitted.
 *
 * In a regular iOS SAFARI TAB the anchor download works silently (straight
 * into Files → Downloads, no dialog), so for speed we use it there — same
 * zero-tap behavior as Android.
 *
 * Returns 'needs-gesture' when iOS refuses the share because the user
 * activation expired (share was called too long after the originating tap)
 * or the user dismissed the sheet — the caller must then re-invoke this from
 * a fresh tap (e.g. a "Save photo" button).
 *
 * Everywhere else this falls through to the classic anchor download.
 */
export async function saveImage(source: string, filename: string): Promise<SaveImageResult> {
  if (isIOS() && isStandalonePWA() && typeof navigator.share === 'function' && source.startsWith('data:')) {
    const blob = dataURLToBlob(source);
    if (blob) {
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
      const canShareFiles =
        typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] });
      if (canShareFiles) {
        try {
          await navigator.share({ files: [file] });
          return 'saved';
        } catch (err) {
          // AbortError = user closed the sheet without saving;
          // NotAllowedError = activation expired. Both need a fresh tap —
          // silently dropping the photo here would lose the user's work.
          const name = err instanceof DOMException ? err.name : '';
          void name;
          return 'needs-gesture';
        }
      }
    }
  }

  downloadImage(source, filename);
  return 'saved';
}

/** Convert a data URL to a Blob without `fetch()` (which can fail on huge URLs in iOS Safari). */
export function dataURLToBlob(dataURL: string): Blob | null {
  try {
    const [header, body] = dataURL.split(',');
    if (!header || body == null) return null;
    const mimeMatch = header.match(/data:([^;]+)/);
    const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
    const isBase64 = /;base64/i.test(header);
    const binary = isBase64 ? atob(body) : decodeURIComponent(body);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  } catch {
    return null;
  }
}

/**
 * Copy text to the clipboard across browsers. Uses the async Clipboard API when
 * available (and in a secure context), otherwise falls back to a hidden
 * `<textarea>` + `execCommand('copy')` for older Safari / non-HTTPS / WebViews.
 * Resolves `true` on success, `false` if every method fails.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to legacy path */
    }
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    // Keep it off-screen and non-disruptive (no scroll jump, no focus flash).
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
